/* JC Date - Dogwood custom date picker (Sep 26 2026).
   The browser's own calendar popup on <input type="date"> cannot be
   styled, so this upgrades a <jc-date> host that wraps a native date
   input: the native input stays in the DOM for the form, the app and
   any min/max validation (assets/jc-date-limits.js), but is taken out
   of view; a trigger button shows the chosen date in words and opens a
   month grid drawn in the theme's fonts and colours. Same pattern and
   same look as JC Select (assets/jc-select.js / .css).

   Markup (built by assets/jc-app-fields.js for app-rendered fields, or
   by any snippet that wants a branded date field):
     <jc-date class="jc-date">
       <input type="date" class="jc-date__native" ...>
     </jc-date>
   Everything else is created here. Reads min and max off the native
   input at open time, so the season cap lives in one place. Writes the
   value back as YYYY-MM-DD and fires input + change, so the app and the
   date-limit check see every choice. Without JavaScript the native
   field shows as usual. Styles: assets/jc-date-picker.css. */
(function () {
  if (customElements.get('jc-date')) return;

  var MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July',
    'August', 'September', 'October', 'November', 'December'];
  var DAYS = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa'];
  var DAYS_LONG = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
  var ICON_CAL = '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">'
    + '<rect x="1.5" y="2.5" width="11" height="10" rx="1.5" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1)"/>'
    + '<path d="M1.5 5.5H12.5M4.5 1V3.5M9.5 1V3.5" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1)" stroke-linecap="round"/></svg>';
  var ICON_CARET = '<svg aria-hidden="true" width="14" height="14" viewBox="0 0 14 14" fill="none" xmlns="http://www.w3.org/2000/svg">'
    + '<path d="M11 5.5L7 9.5L3 5.5" stroke="currentColor" stroke-width="var(--icon-stroke-width, 1)" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var counter = 0;

  /* Dates are handled as plain YYYY-MM-DD strings and local Date objects
     built from their parts, never from Date.parse, so no timezone shift. */
  function pad(n) { return (n < 10 ? '0' : '') + n; }
  function iso(d) { return d.getFullYear() + '-' + pad(d.getMonth() + 1) + '-' + pad(d.getDate()); }
  function fromIso(s) {
    if (!s || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
    var p = s.split('-');
    var d = new Date(+p[0], +p[1] - 1, +p[2]);
    return isNaN(d.getTime()) ? null : d;
  }
  function today() { var d = new Date(); return new Date(d.getFullYear(), d.getMonth(), d.getDate()); }
  function words(s) {
    var d = fromIso(s);
    return d ? MONTHS[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear() : '';
  }
  function el(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  class JCDate extends HTMLElement {
    connectedCallback() {
      if (this.abort) return;
      this.abort = new AbortController();
      var opts = { signal: this.abort.signal };
      this.build();

      this.addEventListener('click', (e) => this.onClick(e), opts);
      this.addEventListener('keydown', (e) => this.onKeydown(e), opts);
      this.addEventListener('change', (e) => { if (e.target === this.native) this.sync(); }, opts);
      this.addEventListener('input', (e) => { if (e.target === this.native) this.sync(); }, opts);
      /* A label click or a script focusing the hidden input lands on the trigger. */
      this.addEventListener('focusin', (e) => {
        if (e.target === this.native && this.trigger) this.trigger.focus();
      }, opts);
      /* Browser validation on the hidden input becomes an inline message. */
      this.addEventListener('invalid', (e) => {
        if (e.target !== this.native) return;
        e.preventDefault();
        var n = this.native;
        this.showError(n.validity.valueMissing ? 'Please choose a date' : (n.validationMessage || 'Please choose a date'));
        var form = this.native.form;
        if (this.trigger && (!form || form.querySelector(':invalid') === this.native)) this.trigger.focus();
      }, true);

      this.onDocClick = (e) => { if (!this.contains(e.target)) this.close(false); };
      this.observer = new MutationObserver(() => {
        this.sync();
        if (this.isOpen) this.render();
      });
      this.observer.observe(this.native, { attributes: true, attributeFilter: ['value', 'min', 'max', 'disabled'] });
      this.sync();
    }

    disconnectedCallback() {
      if (this.abort) this.abort.abort();
      this.abort = null;
      document.removeEventListener('click', this.onDocClick);
      if (this.observer) this.observer.disconnect();
    }

    get native() { return this.querySelector('.jc-date__native'); }
    get trigger() { return this.querySelector('.jc-date__trigger'); }
    get popup() { return this.querySelector('.jc-date__popup'); }
    get valueEl() { return this.querySelector('.jc-date__value'); }
    get errorEl() { return this.querySelector('.jc-date__error'); }
    get isOpen() { var t = this.trigger; return !!t && t.getAttribute('aria-expanded') === 'true'; }

    get min() { var n = this.native; return n ? fromIso(n.min) : null; }
    get max() { var n = this.native; return n ? fromIso(n.max) : null; }
    get placeholder() {
      var n = this.native;
      return (n && (n.dataset.jcPlaceholder || n.getAttribute('placeholder'))) || 'Select a date';
    }

    build() {
      var n = this.native;
      if (!n || this.trigger) return;
      var id = n.id || ('jc-date-' + (++counter));
      var label = this.labelText(n);

      var trigger = el('button', 'jc-date__trigger');
      trigger.type = 'button';
      trigger.id = id + '-trigger';
      trigger.setAttribute('aria-haspopup', 'dialog');
      trigger.setAttribute('aria-expanded', 'false');
      trigger.setAttribute('aria-controls', id + '-popup');
      if (label) trigger.setAttribute('aria-label', label);
      trigger.appendChild(el('span', 'jc-date__value'));
      trigger.appendChild(el('span', 'jc-date__icon', ICON_CAL));
      if (n.disabled) trigger.disabled = true;

      var popup = el('div', 'jc-date__popup');
      popup.id = id + '-popup';
      popup.setAttribute('role', 'dialog');
      popup.setAttribute('aria-modal', 'false');
      popup.setAttribute('aria-label', (label ? label + ': ' : '') + 'choose a date');
      popup.hidden = true;
      popup.innerHTML =
        '<div class="jc-date__head">'
        + '<button type="button" class="jc-date__nav jc-date__nav--prev" aria-label="Previous month">' + ICON_CARET + '</button>'
        + '<span class="jc-date__month" aria-live="polite"></span>'
        + '<button type="button" class="jc-date__nav jc-date__nav--next" aria-label="Next month">' + ICON_CARET + '</button>'
        + '</div>'
        + '<div class="jc-date__weekdays" aria-hidden="true">' + DAYS.map(function (d) { return '<span>' + d + '</span>'; }).join('') + '</div>'
        + '<div class="jc-date__days" role="grid"></div>'
        + '<div class="jc-date__foot">'
        + '<button type="button" class="jc-date__link jc-date__clear">Clear</button>'
        + '<button type="button" class="jc-date__link jc-date__today">Today</button>'
        + '</div>';

      var err = el('span', 'jc-date__error');
      err.setAttribute('role', 'alert');
      err.hidden = true;

      this.appendChild(trigger);
      this.appendChild(popup);
      this.appendChild(err);
    }

    labelText(n) {
      var lbl = n.id ? document.querySelector('label[for="' + CSS.escape(n.id) + '"]') : null;
      if (!lbl) lbl = n.closest('label');
      var t = lbl ? lbl.textContent.replace(/\*/g, '').trim() : '';
      return t || n.getAttribute('aria-label') || n.name || '';
    }

    /* Mirror the native value into the trigger. */
    sync() {
      var n = this.native, v = this.valueEl;
      if (!n || !v) return;
      var text = words(n.value) || this.placeholder;
      if (v.textContent !== text) v.textContent = text;
      var has = !!words(n.value);
      if (this.classList.contains('jc-date--has-value') !== has) this.classList.toggle('jc-date--has-value', has);
      if (this.trigger && this.trigger.disabled !== n.disabled) this.trigger.disabled = n.disabled;
      if (has) this.clearError();
    }

    onClick(e) {
      var t = e.target;
      if (t.closest('.jc-date__trigger')) { this.toggle(); return; }
      if (t.closest('.jc-date__nav--prev')) { this.shift(-1); return; }
      if (t.closest('.jc-date__nav--next')) { this.shift(1); return; }
      if (t.closest('.jc-date__clear')) { this.setValue(''); this.close(true); return; }
      if (t.closest('.jc-date__today')) {
        var d = today();
        if (this.allowed(d)) { this.setValue(iso(d)); this.close(true); }
        else { this.view = new Date(d.getFullYear(), d.getMonth(), 1); this.render(); }
        return;
      }
      var day = t.closest('.jc-date__day');
      if (day && this.contains(day) && !day.disabled) {
        this.setValue(day.dataset.date);
        this.close(true);
      }
    }

    toggle() { this.isOpen ? this.close(true) : this.open(); }

    open() {
      var t = this.trigger, p = this.popup;
      if (!t || !p || this.isOpen || t.disabled) return;
      var current = fromIso(this.native.value) || this.firstAllowed();
      this.view = new Date(current.getFullYear(), current.getMonth(), 1);
      this.focusDate = current;
      this.render();
      p.hidden = false;
      t.setAttribute('aria-expanded', 'true');
      this.place();
      this.focusDay();
      setTimeout(() => document.addEventListener('click', this.onDocClick), 0);
    }

    close(refocus) {
      var t = this.trigger, p = this.popup;
      if (!t || !p || !this.isOpen) return;
      p.hidden = true;
      t.setAttribute('aria-expanded', 'false');
      document.removeEventListener('click', this.onDocClick);
      if (refocus) t.focus();
    }

    /* Open upward when there is not enough room below the trigger. */
    place() {
      var t = this.trigger, p = this.popup;
      if (!t || !p) return;
      var rect = t.getBoundingClientRect();
      var needed = p.offsetHeight + 16;
      var below = window.innerHeight - rect.bottom;
      this.classList.toggle('jc-date--up', below < needed && rect.top > needed);
    }

    /* The first day a customer may pick: today, or min if later. */
    firstAllowed() {
      var d = today(), min = this.min, max = this.max;
      if (min && min > d) d = min;
      if (max && d > max) d = max;
      return d;
    }

    allowed(d) {
      var min = this.min, max = this.max;
      if (min && d < min) return false;
      if (max && d > max) return false;
      return true;
    }

    shift(months) {
      this.view = new Date(this.view.getFullYear(), this.view.getMonth() + months, 1);
      this.focusDate = null;
      this.render();
      var a = document.activeElement;
      if (!this.popup.contains(a) || a.disabled) this.focusDay();
    }

    setValue(v) {
      var n = this.native;
      if (!n || n.value === v) { this.sync(); return; }
      n.value = v;
      n.dispatchEvent(new Event('input', { bubbles: true }));
      n.dispatchEvent(new Event('change', { bubbles: true }));
      this.sync();
    }

    render() {
      var p = this.popup;
      if (!p || !this.view) return;
      var y = this.view.getFullYear(), m = this.view.getMonth();
      var min = this.min, max = this.max, sel = fromIso(this.native.value), now = today();
      p.querySelector('.jc-date__month').textContent = MONTHS[m] + ' ' + y;

      /* Arrows stop at the first and last month that hold an allowed day. */
      var prev = p.querySelector('.jc-date__nav--prev'), next = p.querySelector('.jc-date__nav--next');
      prev.disabled = !!(min && new Date(y, m, 0) < min);
      next.disabled = !!(max && new Date(y, m + 1, 1) > max);

      var grid = p.querySelector('.jc-date__days');
      var first = new Date(y, m, 1), lead = first.getDay();
      var count = new Date(y, m + 1, 0).getDate();
      var start = new Date(y, m, 1 - lead);
      var cells = Math.ceil((lead + count) / 7) * 7;
      var focus = this.focusDate && this.focusDate.getMonth() === m && this.focusDate.getFullYear() === y ? this.focusDate : null;
      if (focus && !this.allowed(focus)) focus = null;
      if (!focus) {
        var f = (sel && sel.getMonth() === m && sel.getFullYear() === y && this.allowed(sel)) ? sel : null;
        if (!f) {
          for (var k = 1; k <= count; k++) { var c = new Date(y, m, k); if (this.allowed(c)) { f = c; break; } }
        }
        focus = f || first;
      }
      this.focusDate = focus;

      var html = '';
      for (var i = 0; i < cells; i++) {
        var d = new Date(start.getFullYear(), start.getMonth(), start.getDate() + i);
        var inMonth = d.getMonth() === m;
        var ok = inMonth && this.allowed(d);
        var cls = 'jc-date__day' + (inMonth ? '' : ' jc-date__day--outside')
          + (sel && iso(d) === iso(sel) ? ' is-selected' : '')
          + (iso(d) === iso(now) ? ' is-today' : '');
        var focusable = inMonth && iso(d) === iso(focus);
        html += '<button type="button" class="' + cls + '" data-date="' + iso(d) + '"'
          + (ok ? '' : ' disabled')
          + ' tabindex="' + (focusable ? '0' : '-1') + '"'
          + ' aria-label="' + DAYS_LONG[d.getDay()] + ', ' + MONTHS[d.getMonth()] + ' ' + d.getDate() + ', ' + d.getFullYear() + '"'
          + (sel && iso(d) === iso(sel) ? ' aria-pressed="true"' : '')
          + '>' + (inMonth ? d.getDate() : '') + '</button>';
      }
      grid.innerHTML = html;
    }

    /* Keyboard: arrows move a day or a week, PageUp/Down a month,
       Home/End to the ends of the week, Enter or Space picks, Escape
       closes. Travel is clamped to the allowed range (greyed days are
       disabled buttons and cannot take focus). */
    moveFocus(days, months) {
      var f = this.focusDate || this.firstAllowed();
      var d = months ? new Date(f.getFullYear(), f.getMonth() + months, f.getDate())
        : new Date(f.getFullYear(), f.getMonth(), f.getDate() + days);
      var min = this.min, max = this.max;
      if (min && d < min) d = min;
      if (max && d > max) d = max;
      this.focusDate = d;
      this.view = new Date(d.getFullYear(), d.getMonth(), 1);
      this.render();
      this.focusDay();
    }

    focusDay() {
      var b = this.popup.querySelector('.jc-date__day[tabindex="0"]');
      if (b && !b.disabled) b.focus();
    }

    onKeydown(e) {
      var k = e.key;
      if (e.target.closest('.jc-date__trigger')) {
        if (!this.isOpen && (k === 'ArrowDown' || k === 'ArrowUp' || k === 'Enter' || k === ' ')) {
          e.preventDefault();
          this.open();
        } else if (this.isOpen && k === 'Escape') {
          e.preventDefault();
          this.close(true);
        }
        return;
      }
      if (!this.isOpen || !e.target.closest('.jc-date__popup')) return;
      if (k === 'Escape') { e.preventDefault(); this.close(true); return; }
      if (k === 'Tab') return;
      if (!e.target.closest('.jc-date__day')) return;
      switch (k) {
        case 'ArrowLeft': e.preventDefault(); this.moveFocus(-1); break;
        case 'ArrowRight': e.preventDefault(); this.moveFocus(1); break;
        case 'ArrowUp': e.preventDefault(); this.moveFocus(-7); break;
        case 'ArrowDown': e.preventDefault(); this.moveFocus(7); break;
        case 'PageUp': e.preventDefault(); this.moveFocus(0, -1); break;
        case 'PageDown': e.preventDefault(); this.moveFocus(0, 1); break;
        case 'Home': e.preventDefault(); this.moveFocus(-this.focusDate.getDay()); break;
        case 'End': e.preventDefault(); this.moveFocus(6 - this.focusDate.getDay()); break;
        case 'Enter':
        case ' ':
          e.preventDefault();
          if (!e.target.disabled) { this.setValue(e.target.dataset.date); this.close(true); }
          break;
      }
    }

    showError(message) {
      var t = this.trigger, err = this.errorEl;
      this.classList.add('jc-date--invalid');
      if (t) t.setAttribute('aria-invalid', 'true');
      if (err) { err.textContent = message; err.hidden = false; }
    }

    clearError() {
      if (!this.classList.contains('jc-date--invalid')) return;
      var t = this.trigger, err = this.errorEl;
      this.classList.remove('jc-date--invalid');
      if (t) t.removeAttribute('aria-invalid');
      if (err) { err.textContent = ''; err.hidden = true; }
    }
  }

  customElements.define('jc-date', JCDate);
})();
